import { PropsWithChildren, useImperativeHandle } from 'react'

import { useCreation } from 'ahooks'

import { GenericalRecord } from '@eastcoal/common/types/utilities'

import { useCreationForm } from '../../../hooks/useCreationForm'

import { CrudFormStoreContext } from './context/StoreContext'
import { CrudFormStoreProviderProps } from './interface'
import { createCrudFormStore } from './store/createStore'
import { CrudFormStoreUpdater } from './StoreUpdater'
import { resolvePattern } from './utils/resolvePattern'

export const CrudFormStoreProvider = <
  FormData extends GenericalRecord,
  DetailData extends GenericalRecord,
  Context
>({
  ref,
  children,
  /* -------------------------------------------------------------------------- */
  /*                         CrudFormStoreUpdater Props                         */
  /* -------------------------------------------------------------------------- */
  context,
  detailType,
  mode,
  readApi,
  createApi,
  updateApi,
  onSuccess,
  onModeChange,

  /* -------------------------------------------------------------------------- */
  /*                                useCreateForm                               */
  /* -------------------------------------------------------------------------- */
  form: externalForm,
  initialValues,
  values,
  mergeStrategy,
  effects,
  validateFirst
}: PropsWithChildren<
  CrudFormStoreProviderProps<FormData, DetailData, Context>
>) => {
  const form = useCreationForm({
    form: externalForm,
    initialValues,
    values,
    mergeStrategy,
    effects,
    validateFirst,
    pattern: resolvePattern(mode)
  })

  const store = useCreation(
    () =>
      createCrudFormStore<FormData, DetailData, Context>({
        detailType,
        mode,
        form
      }),
    [form]
  )

  useImperativeHandle(ref, () => store.getState())

  return (
    <CrudFormStoreContext value={store}>
      <CrudFormStoreUpdater
        context={context}
        createApi={createApi}
        mode={mode}
        readApi={readApi}
        updateApi={updateApi}
        onModeChange={onModeChange}
        onSuccess={onSuccess}
      />
      {children}
    </CrudFormStoreContext>
  )
}
